import React, { useEffect, useState } from 'react';
import clinicService from '../../../../../services/clinic.service';
import Loading from '../../../../common/Loading';
import { useParams } from 'react-router-dom';
import ThemeConfigurationPanel from './ThemeConfigurationPanel';
import HeaderSettingsPanel from './HeaderSettingsPanel';
import FooterSettingsPanel from './FooterSettingsPanel';

const defaultTheme = {
    // Logo Settings
    logo: {
        alt: 'Clinic Logo',
        url: '<<CLINIC_LOGO>>',
        link: '#',
        width: 'auto',
        height: '60px',
        position: 'left',
        visibleInMobile: true
    },
    // Theme and Color Settings
    theme: {
        mode: 'light',
        colors: {
            primary: '#2E8B57',
            primaryLight: '#3CB371',
            primaryDark: '#228B22',
            secondary: '#696969',
            secondaryLight: '#808080',
            success: '#28a745',
            info: '#17a2b8',
            warning: '#ffc107',
            danger: '#dc3545',
            light: '#f8f9fa',
            dark: '#343a40',
            background: '#ffffff',
            surface: '#fafafa',
            text: '#2c3e50',
            textMuted: '#7f8c8d',
            border: '#bdc3c7'
        },
        fonts: {
            primary: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
            size: {
                small: '0.875rem',
                base: '1.4rem',
                large: '2.125rem',
                xl: '2.25rem'
            }
        },
        borderRadius: '0.5rem',
        shadows: {
            sm: '0 2px 4px 0 rgb(0 0 0 / 0.05)',
            base: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
            lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
        }
    },

    // Header/Navbar Settings
    header: {
        layout: 'default', // default, minimal, centered, stacked
        sticky: true,
        showBorder: true,
        height: 'auto',
        padding: {
            top: '1rem',
            bottom: '1rem'
        },
        textColor: '#ffffff',
        backgroundColor: 'auto', // auto uses primary color
        // Logo Settings
        showLogo: true,
        logoPosition: 'left', // left, center, right
        logoPositionMobile: 'center', // left, center, right (for mobile)
        logoHeight: '60px',
        logoHeightMobile: '40px', // for mobile
        // Clinic Name Settings (shown when logo is hidden)
        showClinicName: true,
        clinicNamePosition: 'left', // left, center, right
        clinicNameSize: '1.5rem', // font size
        // Contact Info Settings (right side)
        showContactInfo: false,
        showContactInfoMobile: false, // show/hide contact info on mobile
        contactInfoPosition: 'right', // right, left
        showAddress: true,
        showPhone: true,
        showFax: false,
        contactInfoTextColor: '#ffffff',
        contactInfoTextSize: '0.875rem',
        // Social Icons Settings
        showSocialIcons: false,
        socialIconsPosition: 'right', // right, left, contactLeft, contactRight
        showSocialIconsMobile: false, // show/hide social icons on mobile
    },

    // Footer Settings
    footer: {
        layout: 'detailed',
        text: '© 2025 <<CLINIC_NAME>>. All rights reserved.',
        showPoweredBy: true,
        poweredByText: 'Powered by Eyeconx',
        showLinks: true,
        links: [
            { text: 'Privacy Policy', url: '#' },
            { text: 'Terms of Service', url: '#' },
            { text: 'Contact Us', url: '#' }
        ],
        showContactInfo: true,
        showClinicHours: true,
        showSocialIcons: false,
        backgroundColor: 'auto',
        textColor: '#ffffff'
    },

    // Header Settings
    header: {
        layout: 'default',
        sticky: true,
        showBorder: true,
        height: 'auto',
        padding: {
            top: '1rem',
            bottom: '1rem'
        },
        textColor: '#ffffff',
        backgroundColor: 'auto',
        showLogo: true,
        logoPosition: 'left',
        logoPositionMobile: 'center',
        logoHeight: '60px',
        logoHeightMobile: '40px',
        showClinicName: true,
        clinicNamePosition: 'left',
        clinicNameSize: '1.5rem',
        showContactInfo: false,
        showContactInfoMobile: false,
        contactInfoPosition: 'right',
        showAddress: true,
        showPhone: true,
        showFax: false,
        showEmail: false,
        showWebsite: false,
        contactInfoTextColor: '#ffffff',
        contactInfoTextSize: '0.875rem',
        showSocialIcons: false,
        socialIconsPosition: 'right',
        showSocialIconsMobile: false
    },

    // Animations Settings
    animations: {
        enabled: true,
        duration: '0.3s',
        easing: 'ease-in-out'
    },

    // Responsive Settings
    responsive: {
        breakpoints: {
            mobile: '576px',
            tablet: '768px',
            desktop: '992px',
            wide: '1200px'
        },
        hideElements: {
            mobileHideTagline: true,
            mobileHideAddress: false,
            mobileHideEmail: true
        }
    },

    // Accessibility Settings
    accessibility: {
        focusVisible: true,
        highContrast: false,
        skipToContent: true,
        announcePageChanges: true
    }

};

function PublicThemeTab() {
    const { clinicId } = useParams();
    const [theme, setTheme] = useState(defaultTheme);
    const [clinicContent, setClinicContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [previewChannel, setPreviewChannel] = useState(null);

    useEffect(() => {
        loadTheme();
        // Set up BroadcastChannel for real-time preview updates
        const channel = new BroadcastChannel('theme-preview');
        setPreviewChannel(channel);
        
        return () => {
            if (channel) channel.close();
        };
    }, [clinicId]);

    useEffect(() => {
        // Broadcast theme changes to preview tab
        if (previewChannel) {
            // Merge clinicContent into theme for preview
            const themeForPreview = {
                ...theme,
                // Use clinic logo if available
                logo: clinicContent?.logo || theme.logo,
                // Add clinic info from clinicContent for preview display
                clinicInfo: clinicContent ? {
                    name: clinicContent.name,
                    address: clinicContent.address,
                    contact: clinicContent.contact,
                    hours: clinicContent.hours,
                    socialMedia: clinicContent.socialMedia
                } : null
            };
            previewChannel.postMessage({ type: 'THEME_UPDATE', theme: themeForPreview, clinicContent });
        }
    }, [theme, clinicContent, previewChannel]);

    const loadTheme = () => {
        setLoading(true);
        setError(null);
        clinicService
            .getPublicTheme(clinicId)
            .then((res) => {
                if (res.data?.themeContent) {
                    setTheme({ ...defaultTheme, ...res.data.themeContent });
                } else {
                    setTheme(defaultTheme);
                }
                if (res.data?.clinicContent) {
                    setClinicContent(res.data.clinicContent);
                }
            })
            .catch(() => {
                setError('Failed to load theme settings.');
                setTheme(defaultTheme);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await clinicService.updatePublicTheme(clinicId, theme);
            alert('Theme settings saved successfully!');
        } catch (err) {
            setError('Failed to save theme settings.');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        if (window.confirm('Are you sure you want to reset to default theme?')) {
            setTheme(defaultTheme);
        }
    };

    const handleChange = (section, subsection, field, value) => {
        setTheme(prev => {
            if (subsection && field) {
                return {
                    ...prev,
                    [section]: {
                        ...prev[section],
                        [subsection]: {
                            ...prev[section][subsection],
                            [field]: value
                        }
                    }
                };
            } else if (subsection) {
                return {
                    ...prev,
                    [section]: {
                        ...prev[section],
                        [subsection]: value
                    }
                };
            }
            return {
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: value
                }
            };
        });
    };

    const handleAddFooterLink = () => {
        setTheme(prev => ({
            ...prev,
            footer: {
                ...prev.footer,
                links: [
                    ...prev.footer.links,
                    { text: 'New Link', url: '#' }
                ]
            }
        }));
    };

    const handleRemoveFooterLink = (index) => {
        setTheme(prev => ({
            ...prev,
            footer: {
                ...prev.footer,
                links: prev.footer.links.filter((_, i) => i !== index)
            }
        }));
    };

    const handleFooterLinkChange = (index, field, value) => {
        setTheme(prev => ({
            ...prev,
            footer: {
                ...prev.footer,
                links: prev.footer.links.map((link, i) => 
                    i === index ? { ...link, [field]: value } : link
                )
            }
        }));
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <div className="card card-padding">
            <div className="card-body">
                <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                    <div>
                        <h5 className="clinic-customization__title mb-1">
                            <i className="bi bi-palette text-primary me-1"></i>
                            Public Theme Settings
                        </h5>
                        <p className="text-muted small mb-0">Customize the public-facing theme for this clinic.</p>
                    </div>
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-outline-info btn-sm"
                            onClick={() => {
                                localStorage.setItem('previewTheme', JSON.stringify(theme));
                                if (previewChannel) {
                                    previewChannel.postMessage({ type: 'THEME_UPDATE', theme });
                                }
                                window.open(`/theme-preview/${clinicId}`, '_blank');
                            }}
                            disabled={saving}
                        >
                            <i className="bi bi-eye me-1"></i>
                            Preview Theme
                        </button>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={handleReset}
                            disabled={saving}
                        >
                            <i className="bi bi-arrow-counterclockwise me-1"></i>
                            Reset to Default
                        </button>
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-lg me-1"></i>
                                    Save Theme
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-warning border-0 shadow-sm mb-3">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {error}
                    </div>
                )}

                <ThemeConfigurationPanel theme={theme} handleChange={handleChange} />

                <HeaderSettingsPanel theme={theme} handleChange={handleChange} />

                <FooterSettingsPanel
                    theme={theme}
                    handleChange={handleChange}
                    handleAddFooterLink={handleAddFooterLink}
                    handleRemoveFooterLink={handleRemoveFooterLink}
                    handleFooterLinkChange={handleFooterLinkChange}
                />

            </div>
        </div>
    );
}

export default PublicThemeTab;
